import React from 'react';
import addThousandSeperator from '@ssg/common/Helpers/addThousandSeperator';
import Table from '@ssg/common/Components/Table';
import TextButton from '@ssg/common/Components/TextButton';
import Modal from '@ssg/common/Components/Modal';
import Input from '@ssg/common/Components/Input';
import Button from '@ssg/common/Components/Button';
import UserContext from '../../../UserContext';
import arraysHasMatchingValue from '@ssg/common/Helpers/arraysHasMatchingValue';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { useForm } from 'react-hook-form';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlagEnums } from '@ssg/common/FeatureFlagEnums';
import { CaseEconomicsOverviewDocument, CaseEconomicsOverviewNewQuery, CaseEconomicsOverviewNewQueryVariables, GetJobLedgerEntrySummaryQuery, Permissions, useCaseEconomicsOverviewNewQuery, useGetAdminSettingsQuery, useGetJobLedgerEntrySummaryQuery, useSetCostEstimateMutation, useSetHoursAvailableMutation, useSetSsgPriceMutation, useUpdateCaseMutation } from '@ssg/common/GraphQL/indexV2';

interface Props {
	caseId: string;
	caseErpId: string;
	caseErpNo: string;
	fixedPriceOnCase: number;
}

interface SSGPriceInput {
	price: number;
}

interface HourlyRatePriceInput {
	hourlyRatePrice: number;
}

interface CostEstimateInput {
	costEstimate: number;
}

type combinedJobLedgerSummaryData = {
	name: string;
	actual: number;
	expected: number | undefined;
	bold: boolean;
	identation: number;
};

interface HoursAvailableInput {
	hoursAvailable: number;
}

function editJobLedgerSummaryData(s: GetJobLedgerEntrySummaryQuery['getJobLedgerEntrySummary'] | null) {
	const array: combinedJobLedgerSummaryData[] = [];
	if (!s || !s) return array;

	const summary = s;

	const push = (
		name: string,
		actual: number,
		expected: number | undefined,
		bold: boolean,
		identation: number,
	) => {
		array.push({ name, actual, expected, bold, identation });
	};

	push('invoicedAmount', summary.invoicedAmount?.actual ?? 0, undefined, summary.invoicedAmount?.bold ?? false, summary.invoicedAmount?.indentation ?? 0);
	push('igvaAmount', summary.igvaAmount?.actual ?? 0, undefined, summary.igvaAmount?.bold ?? false, summary.igvaAmount?.indentation ?? 0);
	push('totalRevenue', summary.totalRevenue?.actual ?? undefined, summary.totalRevenue?.expected ?? 0, summary.totalRevenue?.bold ?? false, summary.totalRevenue?.indentation ?? 0);

	push('material', summary.material?.actual ?? 0, undefined, summary.material?.bold ?? false, summary.material?.indentation ?? 0);
	push('personel', summary.personel?.actual ?? 0, undefined, summary.personel?.bold ?? false, summary.personel?.indentation ?? 0);
	push('substitute', summary.substitute?.actual ?? 0, undefined, summary.substitute?.bold ?? false, summary.substitute?.indentation ?? 0);
	push('subcontractor', summary.subcontractor?.actual ?? 0, undefined, summary.subcontractor?.bold ?? false, summary.subcontractor?.indentation ?? 0);
	push('machine', summary.machine?.actual ?? 0, undefined, summary.machine?.bold ?? false, summary.machine?.indentation ?? 0);
	push('vehicleExpenses', summary.vehicleExpenses?.actual ?? 0, undefined, summary.vehicleExpenses?.bold ?? false, summary.vehicleExpenses?.indentation ?? 0);
	push('storageCosts', summary.storageCosts?.actual ?? 0, undefined, summary.storageCosts?.bold ?? false, summary.storageCosts?.indentation ?? 0);

	push('totalCost', summary.totalCost?.actual ?? 0, summary.totalCost?.expected ?? 0, summary.totalCost?.bold ?? false, summary.totalCost?.indentation ?? 0);
	push('grossProfit', summary.grossProfit?.actual ?? 0, summary.grossProfit?.expected ?? 0, summary.grossProfit?.bold ?? false, summary.grossProfit?.indentation ?? 0);
	push('grossProfitPct', summary.grossProfitPct?.actual ?? 0, summary.grossProfitPct?.expected ?? 0, summary.grossProfitPct?.bold ?? false, summary.grossProfitPct?.indentation ?? 0);

	return array;
}

const EconomyTabNew: React.FC<Props> = ({ caseId, caseErpId, caseErpNo, fixedPriceOnCase }): React.ReactElement => {
	const { t } = useTranslation();

	const lockCaseHourlyRate = useFlag(FeatureFlagEnums.LOCK_CASE_HOURLY_RATE);
	const minCaseHourlyRateFlag = useFlag(FeatureFlagEnums.ADMIN_SET_MIN_CASE_HOURLY_RATE);
	const IGVA2024 = useFlag(FeatureFlagEnums.IGVA2024);

	const userContext = React.useContext(UserContext);
	const userPermissions = userContext.user?.permissions ?? [];

	const canSetSSGPrice = arraysHasMatchingValue(userPermissions as unknown as Permissions[], [Permissions.CasesEconomyInvoicesEdit]);

	const [showSSGsalesPrice, setShowSSGSalesPrice] = React.useState(false);
	const [showLockHourlyRateOnCase, setShowLockHourlyRateOnCase] = React.useState(false);
	const [minimumHourlyRate, setMinimumHourlyRate] = React.useState(0);
	const [showBelforCostPriceInput, setShowBelforCostPriceInput] = React.useState(false);
	const [showHoursAvailable, setShowHoursAvailable] = React.useState(false);

	useGetAdminSettingsQuery({
		variables: {
			type: 'INVOICE',
		},
		onCompleted(data) {
			if ('minHourlyRate' in data.setting[0]) {
				setMinimumHourlyRate(data.setting[0].minHourlyRate ?? 0);
			}
		},
	});

	const { data, loading } = useCaseEconomicsOverviewNewQuery({
		context: { debatch: true },
		variables: {
			caseERPReferenceNo: caseErpNo,
		},
		fetchPolicy: 'cache-and-network',
	});

	const { data: ledgerEntrySummaryData, loading: ledgerEntrySummaryLoading } = useGetJobLedgerEntrySummaryQuery({
		context: { debatch: true },
		variables: {
			caseERPReferenceNo: caseErpNo,
		},
		fetchPolicy: 'cache-and-network',
	});

	const [setSSGPrice, { loading: ssgPriceLoading }] = useSetSsgPriceMutation();

	const [setCostEstimate, { loading: costEstimateLoading }] = useSetCostEstimateMutation();

	const [setHoursAvailable, { loading: hoursAvailableLoading }] = useSetHoursAvailableMutation();

	const [updateCase, { loading: updateCaseSubmitting }] = useUpdateCaseMutation();

	const { handleSubmit, register } = useForm<SSGPriceInput>({
		mode: 'all',
		reValidateMode: 'onChange',
	});

	const onSubmit = async (data: SSGPriceInput) => {
		try {
			await setSSGPrice({
				variables: {
					erpNo: caseErpNo,
					price: parseFloat(data.price.toString()),
				},
				update: (cache, { data: cacheData }): void => {
					if (typeof cacheData === 'undefined' || cacheData === null) {
						return;
					}
					const cachedRequest = cache.readQuery<CaseEconomicsOverviewNewQuery, CaseEconomicsOverviewNewQueryVariables>({
						query: CaseEconomicsOverviewDocument,
						variables: {
							caseERPReferenceNo: caseErpNo,
						},
					});

					if (cachedRequest === null || cachedRequest.caseEconomicsOverviewNew === null) {
						return;
					}

					cache.writeQuery<CaseEconomicsOverviewNewQuery, CaseEconomicsOverviewNewQueryVariables>({
						query: CaseEconomicsOverviewDocument,
						variables: {
							caseERPReferenceNo: caseErpNo,
						},
						data: {
							caseEconomicsOverviewNew: {
								...cachedRequest.caseEconomicsOverviewNew,
								wipLimit: cacheData.setSSGPrice,
							},
						},
					});
				},
			});
			setShowSSGSalesPrice(false);
		} catch (e) {
			console.log(e);
		}
	};

	const { handleSubmit: handleCaseFixedPrice, register: registerCaseFixedPrice } = useForm<HourlyRatePriceInput>({
		mode: 'all',
		reValidateMode: 'onChange',
	});

	const onSubmitCaseFixedPrice = async (data: HourlyRatePriceInput) => {
		try {
			await updateCase({
				variables: {
					id: caseId,
					fixedPriceOnCase: parseFloat(data.hourlyRatePrice.toString()),
				},
			});
			setShowLockHourlyRateOnCase(false);
		} catch (e) {
			console.log(e);
		}
	};

	const { handleSubmit: handleCostEstimate, register: registerCostEstimate } = useForm<CostEstimateInput>({
		mode: 'all',
		reValidateMode: 'onChange',
	});

	const onSubmitCostEstimate = async (data: CostEstimateInput) => {
		try {
			await setCostEstimate({
				variables: {
					erpNo: caseErpNo,
					price: parseFloat(data.costEstimate.toString()),
				},
				update: (cache, { data: cacheData }): void => {
					if (typeof cacheData === 'undefined' || cacheData === null) {
						return;
					}
					const cachedRequest = cache.readQuery<CaseEconomicsOverviewNewQuery, CaseEconomicsOverviewNewQueryVariables>({
						query: CaseEconomicsOverviewDocument,
						variables: {
							caseERPReferenceNo: caseErpNo,
						},
					});

					if (cachedRequest === null || cachedRequest.caseEconomicsOverviewNew === null) {
						return;
					}

					cache.writeQuery<CaseEconomicsOverviewNewQuery, CaseEconomicsOverviewNewQueryVariables>({
						query: CaseEconomicsOverviewDocument,
						variables: {
							caseERPReferenceNo: caseErpNo,
						},
						data: {
							caseEconomicsOverviewNew: {
								...cachedRequest.caseEconomicsOverviewNew,
								costEstimate: cacheData.setCostEstimate,
							},
						},
					});
				},
			});
			setShowBelforCostPriceInput(false);
		} catch (e) {
			console.log(e);
		}
	};

	const { handleSubmit: handleHoursAvailable, register: registerHoursAvailable } = useForm<HoursAvailableInput>({
		mode: 'all',
		reValidateMode: 'onChange',
	});

	const onSubmitHoursAvailable = async (data: HoursAvailableInput) => {
		try {
			await setHoursAvailable({
				variables: {
					erpNo: caseErpNo,
					hoursAvailable: parseFloat(data.hoursAvailable.toString()),
				},
				update: (cache, { data: cacheData }): void => {
					if (typeof cacheData === 'undefined' || cacheData === null) {
						return;
					}
					const cachedRequest = cache.readQuery<CaseEconomicsOverviewNewQuery, CaseEconomicsOverviewNewQueryVariables>({
						query: CaseEconomicsOverviewDocument,
						variables: {
							caseERPReferenceNo: caseErpNo,
						},
					});

					if (cachedRequest === null || cachedRequest.caseEconomicsOverviewNew === null) {
						return;
					}

					cache.writeQuery<CaseEconomicsOverviewNewQuery, CaseEconomicsOverviewNewQueryVariables>({
						query: CaseEconomicsOverviewDocument,
						variables: {
							caseERPReferenceNo: caseErpNo,
						},
						data: {
							caseEconomicsOverviewNew: {
								...cachedRequest.caseEconomicsOverviewNew,
								getHoursAvailable: cacheData.setHoursAvailable,
							},
						},
					});
				},
			});
			setShowHoursAvailable(false);
		} catch (e) {
			console.log(e);
		}
	};

	return (
		<>
			<div className="relative flex w-full flex-row pr-4">
				<div className="flex-grow-default mr-4">
					<Table
						data={editJobLedgerSummaryData(ledgerEntrySummaryData?.getJobLedgerEntrySummary ?? null)}
						keySelector={l => l.name}
						noDataFoundText="No data"
						loading={loading || ledgerEntrySummaryLoading}
						columns={[
							{
								label: 'common.description',
								selectFn: l => <div className={classNames({
									'font-bold': l.bold,
									'pl-4': l.identation > 0,
								})}>{t('case.economy.' + l.name)}</div>,
							},
							{
								label: 'case.economy.actual',
								selectFn: l => addThousandSeperator(l.actual),
							},
							{
								label: 'case.economy.expected',
								selectFn: l => typeof l.expected === 'undefined' ? '-' : addThousandSeperator(l.expected),
							},
						]}
					/>
				</div>

				<div className="flex-shrink-default sticky top-0 mt-11 h-64">
					<table className="text-sm">
						<thead>
							<tr>
								<th></th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{typeof data !== 'undefined' && (
								<tr>
									<td className="py-1">
										<p className="flex items-baseline">
											{t('case.economy.offer/screening')}
										</p>
									</td>
									<td className="py-1 text-right">{data.caseEconomicsOverviewNew.screening > 0 ? addThousandSeperator(data.caseEconomicsOverviewNew.screening) : '-'}</td>
								</tr>
							)}
							{typeof data !== 'undefined' && (
								<tr>
									<td className="py-1">
										<p className="flex items-baseline">
											{t('case.economy.ssgSale')}
											{canSetSSGPrice && <TextButton icon={faEdit} onClick={() => setShowSSGSalesPrice(true)} />}
										</p>
									</td>
									<td className="py-1 text-right">{data.caseEconomicsOverviewNew.wipLimit > 0 ? addThousandSeperator(data.caseEconomicsOverviewNew.wipLimit) : '-'}</td>
								</tr>
							)}
							
							{(IGVA2024 && typeof data !== 'undefined') && (
								<>
									<tr>
										<td className="py-1">
											<p className="flex items-baseline">
												{t('case.economy.belforCostPrice')}
												{canSetSSGPrice && <TextButton icon={faEdit} onClick={() => setShowBelforCostPriceInput(true)} />}
											</p>
										</td>
										<td className="py-1 text-right">{data.caseEconomicsOverviewNew.costEstimate > 0 ? addThousandSeperator(data.caseEconomicsOverviewNew.costEstimate) : '-'}</td>
									</tr>
									<tr>
										<td className="py-1">
											<p className="flex items-baseline">
												{t('case.economy.poc')}
											</p>
										</td>
										<td className="py-1 text-right">{data.caseEconomicsOverviewNew.percentageOfCompletion > 0 ? `${data.caseEconomicsOverviewNew.percentageOfCompletion.toFixed(1)}%` : '-'}</td>
									</tr>
								</>
							)}
							<tr>
								<td className="py-1"></td>
								<td className="py-1 text-right"></td>
							</tr>
							{typeof data !== 'undefined' && (
								<tr>
									<td className="py-1">
										<p className="flex items-baseline">
											{t('case.economy.hoursAvailable')}
											{canSetSSGPrice && <TextButton icon={faEdit} onClick={() => setShowHoursAvailable(true)} />}
										</p>
									</td>
									<td className="py-1 text-right">{data.caseEconomicsOverviewNew.getHoursAvailable > 0 ? addThousandSeperator(data.caseEconomicsOverviewNew.getHoursAvailable) : '-'}</td>
								</tr>
							)}
							{typeof data !== 'undefined' && (
								<tr>
									<td className="py-1">{t('case.economy.actualUsedHours')}</td>
									<td className="py-1 text-right">{data.caseEconomicsOverviewNew.getHoursUsed > 0 ? addThousandSeperator(data.caseEconomicsOverviewNew.getHoursUsed) : '-'}</td>
								</tr>
							)}
							<tr>
								<td className="py-1"></td>
								<td className="py-1 text-right"></td>
							</tr>
							<tr>
								<td className="py-1"></td>
								<td className="py-1 text-right"></td>
							</tr>
							{(lockCaseHourlyRate && typeof data !== 'undefined') && (
								<tr>
									<td className="py-1">
										<p className="flex items-baseline">
											{t('case.economy.lockHourlyRate')}
											{canSetSSGPrice && <TextButton icon={faEdit} onClick={() => setShowLockHourlyRateOnCase(true)} />}
										</p>
									</td>
									<td className="py-1 text-right">{fixedPriceOnCase > 0 ? addThousandSeperator(fixedPriceOnCase) : '-'}</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>

			<Modal
				visible={showSSGsalesPrice}
				close={() => setShowSSGSalesPrice(false)}
				title="case.economy.setSsgSale"
				body={
					<form onSubmit={handleSubmit(onSubmit)}>
						<Input name="price" title="case.economy.ssgSale" innerRef={register} type="number" required step={0.01} lang="da" inputMode="decimal" />
						<div className="mt-3">
							<Button submit success text="case.economy.setSsgSale" loading={ssgPriceLoading} />
						</div>
					</form>
				}
			/>

			<Modal
				visible={showLockHourlyRateOnCase}
				close={() => setShowLockHourlyRateOnCase(false)}
				title="case.economy.lockHourlyRate"
				body={
					<form onSubmit={handleCaseFixedPrice(onSubmitCaseFixedPrice)}>
						<Input
							name="hourlyRatePrice"
							title="case.economy.hourlyRateOnCase"
							innerRef={registerCaseFixedPrice}
							type="number"
							required
							step={0.01}
							lang="da"
							inputMode="decimal"
							min={minimumHourlyRate}
						/>
						{(minCaseHourlyRateFlag && (minimumHourlyRate > 0)) && <p>{t('case.economy.minHourlyRate')}: {minimumHourlyRate}</p>}
						<div className="mt-3">
							<Button submit success text="case.economy.setHourlyRate" loading={updateCaseSubmitting} />
						</div>
					</form>
				}
			/>

			<Modal
				visible={showBelforCostPriceInput}
				close={() => setShowBelforCostPriceInput(false)}
				title="case.economy.belforCostPrice"
				body={
					<form onSubmit={handleCostEstimate(onSubmitCostEstimate)}>
						<Input name="costEstimate" title="case.economy.belforCostPrice" innerRef={registerCostEstimate} type="number" required step={0.01} lang="da" inputMode="decimal" />
						<div className="mt-3">
							<Button submit success text="case.economy.setBelforCostPrice" loading={costEstimateLoading} />
						</div>
					</form>
				}
			/>

			<Modal
				visible={showHoursAvailable}
				close={() => setShowHoursAvailable(false)}
				title="case.economy.setHoursAvailable"
				body={
					<form onSubmit={handleHoursAvailable(onSubmitHoursAvailable)}>
						<Input name="hoursAvailable" title="case.economy.hoursAvailable" innerRef={registerHoursAvailable} type="number" required step={0.01} lang="da" inputMode="decimal" />
						<div className="mt-3">
							<Button submit success text="case.economy.setHoursAvailable" loading={hoursAvailableLoading} />
						</div>
					</form>
				}
			/>
		</>
	);
};

export default EconomyTabNew;
